import { differenceInHours, differenceInSeconds } from "date-fns";
import invariant from "invariant";
import { DateTime, Duration } from "luxon";

export { DateTime };

const DEFAULT_TIMEZONE = "Etc/UTC";

const currentBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Returns the current time in milliseconds
 *
 * @param timezone - The timezone to use. Defaults to UTC.
 * @returns The current time in milliseconds
 */
const now = (timezone: string = DEFAULT_TIMEZONE): number => {
  return DateTime.fromMillis(DateTime.now().toMillis(), {
    zone: timezone,
  }).toMillis();
};

const currentDate = (timezone: string = DEFAULT_TIMEZONE): DateTime => {
  return DateTime.now().setZone(timezone);
};

const date = (
  millis: number | string,
  timezone = DEFAULT_TIMEZONE,
): DateTime => {
  if (typeof millis === "string") millis = Number(millis);
  return DateTime.fromMillis(millis, { zone: timezone });
};

const hasTakenLongerThan = ({
  createdAt,
  durationMs,
}: {
  createdAt: Date;
  durationMs: number;
}) => {
  return new Date().getTime() - createdAt.getTime() > durationMs;
};

/**
 * https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
const format = (
  millis: number | string,
  format = "DD",
  timezone: string = DEFAULT_TIMEZONE,
): string => {
  if (typeof millis === "string") millis = Number(millis);
  return date(millis, timezone).toFormat(format);
};

function formatConversationTime(echoMillis: number): string {
  const inputDate = new Date(echoMillis);
  const today = new Date();

  // Check if the input date is today
  if (
    new Date(echoMillis).setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)
  ) {
    // Format as time if the date is today
    let hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? "0" + minutes : minutes.toString();

    return `${hours}:${minutesStr} ${ampm}`;
  } else {
    // Format as abbreviated month and day if the date is not today
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[inputDate.getMonth()];
    const day = inputDate.getDate();

    return `${month} ${day}`;
  }
}

/**
 * Formats a date to a day precision.
 * @param date - The date to format
 * @returns The formatted date in format YYYY-MM-DD (eg "2024-01-01")
 */
const formatToDay = (date: Date): string => date.toLocaleDateString("en-CA");

/**
 * Formats a date to an ISO string with hour precision.
 * Passing this back into the date constructor will give an invalid date, we only use this for redis keys
 * to avoid ":" in the key (which act as a separator in redis).
 * @param date - The date to format
 * @returns The formatted date in format YYYY-MM-DDTHHZ (eg "2024-01-01T11Z")
 */
const formatToISOHour = (date: Date): string => {
  return date.toISOString().replace(/:\d{2}:\d{2}\.\d{3}Z$/, "Z");
};

const minutesBack = (minutes: number): Date => {
  return addMinutes(new Date(), -minutes);
};

const hoursBack = (hours: number): Date => {
  return addHours(new Date(), -hours);
};

const getDaysBackFromMilliseconds = (timeInMs: number): number => {
  const currentTimeMs = Date.now();
  const differenceMs = currentTimeMs - timeInMs;
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.floor(differenceMs / dayMs);
};

const msFromMinutes = (minutes: number): number => {
  return minutes * 60 * 1000;
};

const daysToNanoseconds = (days: number): number => {
  return days * 24 * 60 * 60 * 1000 * 1000000;
};

const daysToMilliseconds = (days: number): number => {
  return days * 24 * 60 * 60 * 1000;
};

const daysToSeconds = (days: number): number => {
  return days * 24 * 60 * 60;
};

const daysToMinutes = (days: number): number => {
  return days * 24 * 60;
};

const daysToHours = (days: number): number => {
  return days * 24;
};

const hoursToNanoseconds = (hours: number): number => {
  return hours * 60 * 60 * 1000 * 1000000;
};

const hoursToMilliseconds = (hours: number): number => {
  return hours * 60 * 60 * 1000;
};

const hoursToSeconds = (hours: number): number => {
  return hours * 60 * 60;
};

const hoursToMinutes = (hours: number): number => {
  return hours * 60;
};

const minutesToNanoseconds = (minutes: number): number => {
  return minutes * 60 * 1000 * 1000000;
};

const minutesToMilliseconds = (minutes: number): number => {
  return minutes * 60 * 1000;
};

const minutesToSeconds = (minutes: number): number => {
  return minutes * 60;
};

const minutesToHours = (minutes: number): number => {
  return minutes / 60;
};

const secondsToNanoseconds = (seconds: number): number => {
  return seconds * 1000 * 1000000;
};

const secondsToMilliseconds = (seconds: number): number => {
  return seconds * 1000;
};

const secondsToMinutes = (seconds: number): number => {
  return seconds / 60;
};

const secondsToHours = (seconds: number): number => {
  return seconds / 60 / 60;
};

const secondsToDays = (seconds: number): number => {
  return seconds / 60 / 60 / 24;
};

const millisecondsToNanoseconds = (milliseconds: number): number => {
  return milliseconds * 1000000;
};

const millisecondsToSeconds = (milliseconds: number): number => {
  return milliseconds / 1000;
};

const millisecondsToMinutes = (milliseconds: number): number => {
  return milliseconds / 1000 / 60;
};

const millisecondsToHours = (milliseconds: number): number => {
  return milliseconds / 1000 / 60 / 60;
};

const nanosecondsToMilliseconds = (nanoseconds: number): number => {
  return nanoseconds / 1000000;
};

const hoursToDays = (hours: number): number => {
  return hours / 24;
};

const floorToDay = (date: Date) => {
  const dateCopy = new Date(date);
  dateCopy.setUTCHours(0, 0, 0, 0);
  return dateCopy;
};

const floorToHour = (date: Date) => {
  const dateCopy = new Date(date);
  dateCopy.setUTCMinutes(0, 0, 0);
  return dateCopy;
};

/**
 * Floors a date to the nearest minutesBack interval.
 *
 * e.g. if current time is 14:37:45:
 * floorToMinutes(5)   -> returns 14:35:00 (rounds down to nearest 5 min)
 * floorToMinutes(10)  -> returns 14:30:00 (rounds down to nearest 10 min)
 * floorToMinutes(15)  -> returns 14:30:00 (rounds down to nearest 15 min)
 * floorToMinutes(30)  -> returns 14:30:00 (rounds down to nearest 30 min)
 *
 * @param date Date to floor (defaults to current date if not provided)
 * @returns Date floored to the nearest minutesBack interval
 */
const floorToMinutes = (minutesBack: number, date = new Date()) => {
  const dateCopy = new Date(date);
  const minutes = dateCopy.getUTCMinutes();
  const flooredMinutes = Math.floor(minutes / minutesBack) * minutesBack;
  dateCopy.setUTCMinutes(flooredMinutes, 0, 0);
  return dateCopy;
};

/**
 * Add a specific number of days to a date. Pass a negative number to take away days from the date.
 * Fractional days are rounded to the nearest hour (adding 1.5 days is the same as 1.52, which will both round to 36 hours).
 * @param date Date to manipulate
 * @param hours Number of hours
 */
const addDays = (date: Date, days: number) => {
  const dateCopy = new Date(+date);

  const flooredDays = Math.floor(days);
  const hours = (days - flooredDays) * 24;
  dateCopy.setDate(dateCopy.getDate() + flooredDays);
  dateCopy.setHours(dateCopy.getHours() + hours);
  return dateCopy;
};

/**
 * Add a specific number of hours to a date. Pass a negative number to take away hours from the date.
 * Fractional hours are rounded to the nearest hour.
 * @param date Date to manipulate
 * @param hours Number of hours
 */
const addHours = (date: Date, hours: number) => {
  const dateCopy = new Date(+date);
  dateCopy.setHours(dateCopy.getHours() + hours);
  return dateCopy;
};

/**
 * Add a specific number of minutes to a date. Pass a negative number to take away minutes from the date.
 * Fractional minutes are rounded to the nearest minute.
 * @param date Date to manipulate
 * @param minutes Number of minutes
 */
const addMinutes = (date: Date, minutes: number) => {
  const dateCopy = new Date(+date);
  dateCopy.setMinutes(dateCopy.getMinutes() + minutes);
  return dateCopy;
};

/**
 * Add a specific number of seconds to a date. Pass a negative number to take away seconds from the date.
 * Fractional seconds are rounded to the nearest second.
 * @param date Date to manipulate
 * @param seconds Number of seconds
 */
const addSeconds = (date: Date, seconds: number) => {
  const dateCopy = new Date(+date);
  dateCopy.setSeconds(dateCopy.getSeconds() + seconds);
  return dateCopy;
};

const getHourlyDates = (startDate: Date, endDate: Date) => {
  const startHour = floorToHour(startDate);
  const endHour = floorToHour(endDate);
  const dates = [];
  for (let date = startHour; date <= endHour; date = addHours(date, 1)) {
    dates.push(date);
  }
  return dates;
};

const getPreviousCompleted24HourlyDates = (): {
  range: Date[];
  startDate: Date;
  endDate: Date;
} => {
  const now = new Date();
  const endHour = time.floorToHour(time.addHours(now, -1));
  const startHour = time.addHours(endHour, -23);
  const range = time.getHourlyDates(startHour, endHour);
  invariant(range.length === 24, "range.length === 24");
  return {
    range: time.getHourlyDates(startHour, endHour),
    startDate: startHour,
    endDate: endHour,
  };
};

const getDailyDates = (startDate: Date, endDate: Date) => {
  const startDay = floorToDay(startDate);
  const endDay = floorToDay(endDate);
  const dates = [];
  for (let date = startDay; date <= endDay; date = addDays(date, 1)) {
    dates.push(date);
  }
  return dates;
};

const getDateDaysBack = (days: number) => {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1_000);
};

const isDateInFuture = (date: Date) => {
  return date > new Date();
};

const isNextHoursUTC = ({
  currentMs,
  earlierMs,
  minDifferenceInHours,
}: {
  currentMs: number;
  earlierMs: number;
  minDifferenceInHours: number;
}): boolean => {
  return (
    differenceInHours(new Date(currentMs), new Date(earlierMs)) >=
    minDifferenceInHours
  );
};

const isNextSecondsUTC = ({
  currentMs,
  earlierMs,
  minDifferenceInSeconds,
}: {
  currentMs: number;
  earlierMs: number;
  minDifferenceInSeconds: number;
}): boolean => {
  return (
    differenceInSeconds(new Date(currentMs), new Date(earlierMs)) >=
    minDifferenceInSeconds
  );
};

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }

  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? "s" : ""}`;
}

export const time = {
  now,
  date,
  hasTakenLongerThan,
  format,
  formatConversationTime,
  formatToDay,
  formatToISOHour,
  currentBrowserTimezone,
  currentDate,
  Duration,
  minutesBack,
  hoursBack,
  getDaysBackFromMilliseconds,
  daysToSeconds,
  msFromMinutes,

  nanosecondsToMilliseconds,

  millisecondsToNanoseconds,
  millisecondsToSeconds,
  millisecondsToMinutes,
  millisecondsToHours,

  secondsToNanoseconds,
  secondsToMilliseconds,
  secondsToMinutes,
  secondsToHours,
  secondsToDays,
  minutesToNanoseconds,
  minutesToMilliseconds,
  minutesToSeconds,
  minutesToHours,

  hoursToNanoseconds,
  hoursToMilliseconds,
  hoursToSeconds,
  hoursToMinutes,
  hoursToDays,

  daysToMilliseconds,
  daysToNanoseconds,
  daysToHours,
  daysToMinutes,

  floorToDay,
  floorToHour,
  floorToMinutes,

  addDays,
  addHours,
  addMinutes,
  addSeconds,

  getHourlyDates,
  getPreviousCompleted24HourlyDates,
  getDailyDates,
  getDateDaysBack,

  isDateInFuture,

  isNextHoursUTC,
  isNextSecondsUTC,

  formatDuration,
};
